import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { BadgeCheck, Loader2, SearchX, AlertTriangle, Ban, ChevronRight } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { fmtDate, fullName } from "@/lib/format";
import { CnapSearch } from "@/components/site/CnapSearch";
import { GreenCard } from "@/components/site/GreenCard";
import { StatusBadge } from "@/components/site/StatusBadge";

const verdict = (ap) => {
  if (ap.statut === "valide") return { icon: BadgeCheck, cls: "text-emerald-700", t: "Inscrit — carte verte valide" };
  if (ap.statut === "expire_bientot") return { icon: AlertTriangle, cls: "text-amber-600", t: `Inscrit — carte verte expire dans ${ap.jours_restants} jour(s)` };
  if (ap.statut === "expiree") return { icon: AlertTriangle, cls: "text-rose-600", t: "Inscrit — carte verte expirée" };
  return { icon: AlertTriangle, cls: "text-slate-600", t: "Inscrit — aucune carte verte délivrée" };
};

const Result = ({ ap }) => {
  const v = verdict(ap);
  return (
    <div data-testid="verify-result" className="rise grid lg:grid-cols-2 gap-10 items-center">
      <GreenCard ap={ap} />
      <div className="space-y-5">
        <div className={`flex items-center gap-2 font-display font-bold text-xl ${v.cls}`}>
          <v.icon className="h-6 w-6" /> <span data-testid="verify-verdict">{v.t}</span>
        </div>
        <dl className="divide-y divide-emerald-900/10 rounded-2xl border border-emerald-900/10 bg-white">
          {[
            ["Nom complet", fullName(ap), "verify-name"],
            ["N° CNAP", ap.cnap, "verify-cnap"],
            ["Date de délivrance", fmtDate(ap.date_delivrance), "verify-issued"],
            ["Date d'expiration", fmtDate(ap.date_expiration), "verify-expires"],
          ].map(([k, val, id]) => (
            <div key={k} className="flex justify-between gap-4 px-5 py-3.5 text-sm">
              <dt className="text-slate-500">{k}</dt>
              <dd data-testid={id} className={`font-semibold text-slate-900 text-right ${id === "verify-cnap" ? "font-mono" : ""}`}>{val}</dd>
            </div>
          ))}
          <div className="flex justify-between items-center px-5 py-3.5 text-sm">
            <dt className="text-slate-500">Statut</dt>
            <dd><StatusBadge statut={ap.statut} testId="verify-status-badge" /></dd>
          </div>
        </dl>
      </div>
    </div>
  );
};

const Masked = ({ ap }) => (
  <div data-testid="verify-masked" className="rise flex items-start gap-4 rounded-2xl border-2 border-rose-300 bg-rose-50 p-6 max-w-2xl">
    <Ban className="h-8 w-8 text-rose-600 shrink-0" />
    <div>
      <div data-testid="verify-masked-label" className="font-display font-extrabold text-xl sm:text-2xl text-rose-700 tracking-tight">PAS EN ORDRE DE COTISATION</div>
      <p className="text-sm text-rose-800 mt-2">{fullName(ap)} — CNAP <span className="font-mono font-bold">{ap.cnap}</span></p>
      <p className="text-sm text-rose-700 mt-1">Veuillez vous rapprocher de l'administration de l'UAPHARCO pour régulariser votre situation.</p>
    </div>
  </div>
);

const ResultsList = ({ items }) => (
  <div data-testid="verify-results-list" className="rise space-y-3 max-w-3xl">
    <p className="text-sm text-slate-500">{items.length} résultat(s) — sélectionnez un AP.</p>
    {items.map((a) => (
      <Link key={a.cnap} to={`/verification?q=${a.cnap}`} data-testid={`verify-result-item-${a.cnap}`} className="flex items-center gap-4 rounded-2xl border border-emerald-900/10 bg-white p-4 hover:border-emerald-700/40 hover:shadow-md transition-[box-shadow,border-color]">
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-slate-900 truncate">{fullName(a)}</div>
          <div className="text-xs font-mono text-emerald-700">{a.cnap}</div>
        </div>
        {a.masque ? <span className="text-[11px] font-bold text-rose-700 border border-rose-300 bg-rose-50 rounded-full px-2 py-0.5">PAS EN ORDRE DE COTISATION</span> : <StatusBadge statut={a.statut} />}
        <ChevronRight className="h-4 w-4 text-slate-400" />
      </Link>
    ))}
  </div>
);

export default function Verify() {
  const { cnap } = useParams();
  const [params] = useSearchParams();
  const q = (params.get("q") || cnap || "").trim();
  const [state, setState] = useState({ loading: false, items: null, error: "" });

  useEffect(() => {
    if (!q) return setState({ loading: false, items: null, error: "" });
    setState({ loading: true, items: null, error: "" });
    api.get("/public/search", { params: { q } })
      .then((r) => setState({ loading: false, items: r.data, error: "" }))
      .catch((e) => setState({ loading: false, items: null, error: errMsg(e) }));
  }, [q]);

  const one = state.items?.length === 1 ? state.items[0] : null;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20 space-y-12">
      <div className="max-w-2xl space-y-5">
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900">Vérification d'un AP</h1>
        <p className="text-slate-600">Recherche en lecture seule par numéro CNAP ou par nom dans le registre de l'UAPHARCO.</p>
        <CnapSearch key={q} initial={q} />
      </div>
      {state.loading && <Loader2 data-testid="verify-loading" className="h-8 w-8 animate-spin text-emerald-700" />}
      {state.error && (
        <div data-testid="verify-not-found" className="rise flex items-start gap-4 rounded-2xl border border-rose-200 bg-rose-50 p-6 max-w-2xl">
          <SearchX className="h-6 w-6 text-rose-600 shrink-0" />
          <div>
            <div className="font-display font-bold text-rose-800">Non trouvé</div>
            <p className="text-sm text-rose-700 mt-1">{state.error}</p>
          </div>
        </div>
      )}
      {one && (one.masque ? <Masked ap={one} /> : <Result ap={one} />)}
      {state.items?.length > 1 && <ResultsList items={state.items} />}
    </div>
  );
}
